import { jsx as _jsx } from "react/jsx-runtime";
/**
 * RatingScale — 1-N selector built from brand-tinted square buttons.
 *
 * Replaces the bespoke buttons in CsatPublic.tsx (audit P1 finding #2):
 * those were hand-rolled with `bg-blue-50 border-blue-500 text-blue-700`
 * which broke the StyxDesk indigo palette and used a custom h-12.
 *
 * Indigo brand colour, radius-control, focus-ring identical to <Button>
 * — a survey rating now feels like a first-class StyxDesk control.
 *
 * Props
 *   value         currently selected value (1..max), or null when unset
 *   onChange      called with the new value when the user picks one
 *   max           highest selectable value (default 5)
 *   min           lowest selectable value (default 1; supports 0-N if needed)
 *   disabled      blocks interaction; visual fade
 *   ariaLabel     announced label for the radiogroup
 *   className     pass-through
 *
 * a11y
 *   Implemented as a `radiogroup` with each cell as a `radio`, so screen
 *   readers announce "1 of 5 selected" rather than "button". Keyboard:
 *   left/right arrow keys move selection; Tab moves focus to the next
 *   form field per WAI-ARIA radio practice.
 */
import { useId } from 'react';
import { cn } from '@/lib/cn';
export function RatingScale({ value, onChange, max = 5, min = 1, disabled = false, ariaLabel = 'Rating', className, }) {
    const groupId = useId();
    const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    const onKeyDown = (e, current) => {
        if (disabled)
            return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            onChange(Math.min(max, current + 1));
        }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            onChange(Math.max(min, current - 1));
        }
    };
    return (_jsx("div", { role: "radiogroup", "aria-label": ariaLabel, id: groupId, className: cn('inline-flex items-center gap-2', className), children: values.map((v) => {
            const selected = value === v;
            return (_jsx("button", { type: "button", role: "radio", "aria-checked": selected, "aria-label": `${v} of ${max}`, disabled: disabled, tabIndex: selected || (value === null && v === min) ? 0 : -1, onClick: () => !disabled && onChange(v), onKeyDown: (e) => onKeyDown(e, v), className: cn(
                // Layout
                'inline-flex h-11 w-11 items-center justify-center radius-control', 'text-base font-semibold transition-all', 
                // Focus ring matches <Button>
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500', 
                // Disabled
                disabled && 'cursor-not-allowed opacity-50', 
                // Selected vs idle styling — uses StyxDesk indigo, NOT blue.
                selected
                    ? 'border-2 border-brand-600 bg-brand-600 text-white shadow-brand'
                    : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-400 hover:bg-brand-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-brand-500 dark:hover:bg-brand-950/30'), children: v }, v));
        }) }));
}
